<template>
  <section id="sl-item-detail" class="master-section">
    <template v-if="detailIsReady">
      <header>
        <h1 class="title">{{title}}</h1>
      </header>
      <section>
        <div class="columns">
          <div class="column col-10 col-md-9 col-sm-8 col-xs-12">
            <div class="form-group">
              <label class="form-label" for="edit-item-category">Category</label>
              <select class="form-select" v-model="item.categoryId" id="edit-item-category">
                <option v-for="c in categories" :key="c.id" :value="c.id">{{c.name}}</option>
              </select>
            </div>
            <div class="form-group" :class="{'has-error': hasNameError}">
              <label class="form-label" for="edit-item-name">Name</label>
              <input id="edit-item-name" class="form-input" type="text" v-model="item.name" ref="name">
            </div>
            <div class="form-group">
              <label class="form-label" for="edit-item-notes">Notes</label>
              <div class="input-group">
                <input id="edit-item-notes" class="form-input" type="text" v-model="item.notes" ref="notes">
                <button @click.stop="clearNotes" class="btn input-group-btn"><fa-icon icon="times-circle"></fa-icon></button>
              </div>
            </div>
            <div class="form-group">
              <div class="columns">
                <div class="column col-xs-12">
                  <div class="form-group">
                    <label class="form-label" for="edit-item-qty">Quantity Needed</label>
                    <div class="input-group">
                      <button @click.stop="quantityDown" class="btn input-group-btn"><fa-icon icon="angle-down" size="lg"></fa-icon></button>
                      <input id="edit-item-qty" type="number" min="0" max="99" step="1" class="form-input text-center" v-model="item.quantityNeeded" placeholder="Quantity">
                      <button @click.stop="quantityUp" class="btn input-group-btn"><fa-icon icon="angle-up" size="lg"></fa-icon></button>
                    </div>
                  </div>
                </div>
                <div class="column">
                  <div class="form-group">
                    <label class="form-label" for="edit-item-price">Price Each</label>
                    <div class="input-group">
                      <span class="input-group-addon">&dollar;</span>
                      <input id="edit-item-price" class="form-input text-right" type="number" step="0.01" min="0" v-model="priceEachDollars" placeholder="Price Each">
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="form-group pt-2">
              <div class="input-group">
                <label class="form-switch" :class="{'has-error': item.isOneTimeItem}">
                  <input type="checkbox" v-model="item.isOneTimeItem">
                  <i class="form-icon"></i>
                  {{ oneTimeLabel }}<span class="status-icon" v-if="item.isOneTimeItem"><fa-icon icon="backspace"></fa-icon></span>
                </label>
              </div>
            </div>
            <div class="form-group pt-2">
              <div class="input-group" >
                <label class="form-switch">
                  <input type="checkbox" v-model="item.isEventualPurchase">
                  <i class="form-icon"></i>
                  {{ eventualLabel }}<span class="status-icon" v-if="item.isEventualPurchase"><fa-icon :icon="['far', 'calendar-alt']"></fa-icon></span>
                </label>
              </div>
            </div>
          </div>
          <div class="column col-2 col-md-3 col-sm-4 col-xs-12">
            <div class="form-group ml-2">
              <label class="form-label">Only Available At:</label>
              <div class="sl-store-list">
                <label class="form-checkbox" v-for="s in stores" :key="s.id" :class="{'active': storeIdMap[s.id]}">
                  <input type="checkbox" :checked="storeIdMap[s.id]" @change="toggleStoreId(s.id)">
                  <i class="form-icon"></i> {{ s.name }}
                </label>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section class="sl-form-controls">
        <div class="controls">
          <button class="btn btn-primary btn-cancel" @click.stop="goBackToFromRoute"><fa-icon icon="times" /><span>Cancel</span></button>
          <button class="btn btn-error" v-if="!isNewItem" @click.stop="deleteItem"><fa-icon icon="trash" /><span>Delete</span></button>
          <button class="btn btn-success" @click.stop="saveChanges"><fa-icon icon="check" /><span>Save</span></button>
        </div>
      </section>
    </template>
    <template v-else>
      <div class="loading loading-xl"></div>
    </template>
  </section>
</template>

<script>
import _ from 'lodash'

export default {
  name: 'ItemDetailView',
  props: {
    id: { type: String, required: false },
    newItemName: { type: String, required: false },
    fromRouteName: { type: String, required: false }
  },
  data () {
    return {
      isNewItem: true,
      item: {},
      hasNameError: false,
      priceEachDollars: '',
      storeIdMap: {}
    }
  },
  computed: {
    detailIsReady () {
      return this.storesAreLoaded && this.itemsAreLoaded && this.categoriesAreLoaded
    },
    title () {
      return this.isNewItem ? 'New Item' : `Edit Item`
    },
    oneTimeLabel () {
      return this.item.isOneTimeItem ? 'Delete after purchase' : 'Permanent item'
    },
    eventualLabel () {
      return this.item.isEventualPurchase ? 'Get it eventually' : 'Get it next time'
    },
    activeStoreIdList () {
      const list = []
      _.forOwn(this.storeIdMap, (isActive, storeId) => {
        if (isActive) {
          list.push(parseInt(storeId, 10))
        }
      })
      return list
    }
  },
  methods: {
    loadItem () {
      const defaultItem = {
        name: this.newItemName || '',
        notes: '',
        quantityNeeded: 1,
        isOneTimeItem: false,
        isEventualPurchase: false,
        categoryId: this.categories && this.categories.length ? this.categories[0].id : -1
      }
      // Make a local copy
      this.item = _.assign({}, this.id ? this.getItemForId(this.id) || defaultItem : defaultItem)
      this.priceEachDollars = this.item.priceEachCents ? (this.item.priceEachCents / 100.0).toFixed(2) : ''
    },
    quantityDown () {
      this.item.quantityNeeded--
      if (this.item.quantityNeeded < 0) {
        this.item.quantityNeeded = 0
      }
    },
    quantityUp () {
      this.item.quantityNeeded++
    },
    clearNotes () {
      this.notes = ''
      this.$refs['notes'].focus()
    },
    toggleStoreId (id) {
      this.$set(this.storeIdMap, id, !this.storeIdMap[id])
    },
    goBackToFromRoute () {
      if (this.fromRouteName) {
        this.$router.push({ name: this.fromRouteName })
      } else {
        this.$router.push({ name: 'home' }) // Go to list
      }
    },
    async deleteItem () {
      // TODO: confirm modal and delete req
    },
    async saveChanges () {
      if (this.hasNameError || !this.item.name) {
        this.hasNameError = true
        this.$refs['name'].focus()
        return
      }

      const itemModel = {
        name: this.item.name,
        notes: (this.item.notes || '').trim(),
        priceEachCents: Math.floor(parseFloat(this.priceEachDollars) * 100),
        quantityNeeded: this.item.quantityNeeded || 0,
        isOneTimeItem: this.item.isOneTimeItem,
        isEventualPurchase: this.item.isEventualPurchase,
        categoryId: this.item.categoryId,
        storeIds: this.activeStoreIdList
      }

      if (this.isNewItem) {
        await this.createItem(itemModel)
      } else {
        itemModel.id = this.item.id
        await this.updateItem(itemModel)
      }
      this.goBackToFromRoute()
    }
  },
  mounted () {
    this.loadItem()
  },
  watch: {
    detailIsReady () {
      this.loadItem()
    },
    item (newItem) {
      if (this.detailIsReady && !newItem.id) {
        if (this.id) {
          // Can't find an item for this id... go to the catalog page
          this.$router.push({ name: 'catalog' })
        }
        this.isNewItem = true
        this.setPageMeta({ pageTitle: 'New Item' })
        return
      }

      // Have an existing item, prime settings
      this.isNewItem = false
      this.setPageMeta({ pageTitle: `Edit ${newItem.name}` })
      this.priceEachDollars = this.item.priceEachCents ? (this.item.priceEachCents / 100.0).toFixed(2) : ''
      this.storeIdMap = {}
      _.forEach(newItem.storeIds, sId => {
        this.storeIdMap[sId] = true
      })
    }
  }
}
</script>

<style lang="scss">
  @import '../assets/_vars';

  #sl-item-detail {
    .input-group-addon {
      border: 1px solid $primary-color;
      font-size: 0.7rem;
      background-color: $light-color;
      color: $dark-color;
    }

    .form-checkbox {
      color: darken($light-color, 20%);
      &.active {
        color: $bg-color;
      }
    }

    .form-switch {
      .status-icon {
        color: $link-color;
        margin-left: 0.5rem;
      }
    }

    .sl-store-list {
      max-height: 16rem;
      overflow-y: auto;
    }

    .col-xs-12 + .col-xs-12 {
      margin-top: 1rem;
    }
  }
</style>
